import React from 'react';
import { Popover, PopoverSurface, PopoverTrigger, mergeClasses } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import './GuideComponents.css';

export interface GuideHelpPopoverProps {
  /** Título bold de la tarjeta (también nombra el diálogo). */
  title: string;
  /** Explicación en texto secundario. */
  text: React.ReactNode;
  /** Link opcional al pie ("Ver guía completa →"): con `href` abre en otra pestaña; si no, `onClick`. */
  link?: { label: string; href?: string; onClick?: () => void };
  /** Nombre accesible del botón (?). Por defecto "Ayuda: {title}". */
  ariaLabel?: string;
  className?: string;
}

/**
 * Popover de ayuda de la guía ("Ayudas y mensajes"): botón (?) circular en primario que, al hacer
 * click, abre una tarjeta con título bold, texto secundario y link opcional. Sin flecha, superficie
 * con borde y radio 4 (alto contraste: borde blanco, foco amarillo).
 */
export const GuideHelpPopover = ({ title, text, link, ariaLabel, className }: GuideHelpPopoverProps) => {
  const { t } = useTranslation();
  return (
    <Popover positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={mergeClasses('ichtys-help-trigger', className)}
          aria-label={ariaLabel ?? (t('guideComponents.help.ariaLabel', { title }) as string)}
        >
          ?
        </button>
      </PopoverTrigger>
      <PopoverSurface className="ichtys-help" aria-label={title}>
        <p className="ichtys-help__title">{title}</p>
        <div className="ichtys-help__text">{text}</div>
        {link &&
          (link.href ? (
            <a className="ichtys-help__link" href={link.href} target="_blank" rel="noopener noreferrer">
              {link.label} →
            </a>
          ) : (
            <button type="button" className="ichtys-help__link" onClick={link.onClick}>
              {link.label} →
            </button>
          ))}
      </PopoverSurface>
    </Popover>
  );
};

export default GuideHelpPopover;
